import { useEffect, useState } from 'react';
import { Icon, V2Button } from '@sakhtar/design-system/react';
import type { CatalogProduct, MasterDataRecord } from '../../lib/types/masterData.js';
import type { SalesDocumentLine } from '../../lib/types/crm.js';
import { faDigits } from '../../lib/faDigits.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiGet } from '../../lib/apiClient.js';
import {
  equivalenceHint,
  lineForProduct,
  lineForUnit,
  priceKey,
  sellableUnits,
  toAsciiDigits,
  type PriceListPrices,
} from '../../lib/productCatalog.js';

/** One editable pre-invoice / order line (strings, as typed). */
export interface QuoteLineDraft {
  productCode: string;
  description: string;
  quantity: string;
  uomCode: string;
  unitPriceRial: string;
  discountRial: string;
  taxCode: string;
}

export const EMPTY_QUOTE_LINE: QuoteLineDraft = {
  productCode: '',
  description: '',
  quantity: '',
  uomCode: '',
  unitPriceRial: '',
  discountRial: '',
  taxCode: '',
};

export function draftsFromLines(lines: readonly SalesDocumentLine[] | undefined): QuoteLineDraft[] {
  if (!lines || lines.length === 0) return [{ ...EMPTY_QUOTE_LINE }];
  return lines.map((line) => ({
    productCode: line.productRef,
    description: line.description,
    quantity: line.quantity,
    uomCode: line.uomRef,
    unitPriceRial: line.unitPriceRial,
    discountRial: line.discountRial === '0' ? '' : line.discountRial,
    taxCode: line.taxRef ?? '',
  }));
}

/** Lines the server will accept (product, quantity and unit are mandatory). */
export function payloadLines(lines: readonly QuoteLineDraft[]) {
  return lines
    .filter((l) => l.productCode && l.quantity && l.uomCode)
    .map((l) => ({
      productRef: l.productCode,
      description: l.description.trim() || undefined,
      quantity: l.quantity,
      uomRef: l.uomCode,
      unitPriceRial: l.unitPriceRial.trim() || undefined,
      discountRial: l.discountRial.trim() || undefined,
      taxRef: l.taxCode || undefined,
    }));
}

interface PriceListOption {
  readonly priceListId: string;
  readonly code: string;
  readonly name: string;
}

interface PriceListDetail {
  readonly lines: readonly { productCode: string; uomCode: string; unitPriceRial: string }[];
}

export function QuoteLinesEditor({
  base,
  lines,
  onChange,
  products,
  taxes,
  showVat,
  unitLabel,
}: {
  /** Tenant base path — used to offer the tenant's price lists. */
  readonly base: string;
  readonly lines: readonly QuoteLineDraft[];
  readonly onChange: (next: QuoteLineDraft[]) => void;
  /** Catalogue products: each carries its own sale units, equivalences and list prices. */
  readonly products: readonly CatalogProduct[];
  readonly taxes: readonly MasterDataRecord[];
  readonly showVat: boolean;
  readonly unitLabel: string;
}) {
  const [categoryId, setCategoryId] = useState('');
  const [priceListId, setPriceListId] = useState('');
  const [prices, setPrices] = useState<PriceListPrices>(new Map());
  const [priceListError, setPriceListError] = useState(false);
  // Price lists valid today. Hidden entirely when the user cannot read them.
  const priceLists = useApiGet<{ items: readonly PriceListOption[] }>(
    base
      ? `${base}/api/v1/master-data/price-lists?status=active&effectiveOn=today&limit=100`
      : null,
    [base],
  );
  const availableLists = priceLists.status === 'ready' ? priceLists.data.items : [];

  // Choosing a list loads its prices and re-prices the lines it covers (an explicit action);
  // products it does not list keep the price they already have.
  useEffect(() => {
    if (!priceListId) {
      setPrices(new Map());
      setPriceListError(false);
      return;
    }
    let cancelled = false;
    void apiGet<PriceListDetail>(`${base}/api/v1/master-data/price-lists/${priceListId}`)
      .then((detail) => {
        if (cancelled) return;
        const map = new Map(
          detail.lines.map((l) => [priceKey(l.productCode, l.uomCode), l.unitPriceRial]),
        );
        setPrices(map);
        setPriceListError(false);
        onChange(
          lines.map((line) => {
            const listed = map.get(priceKey(line.productCode, line.uomCode));
            return listed === undefined ? line : { ...line, unitPriceRial: listed };
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setPriceListError(true);
      });
    return () => {
      cancelled = true;
    };
    // Re-price only when the chosen list changes, not on every keystroke in a line.
  }, [priceListId]);

  const update = (index: number, patch: Partial<QuoteLineDraft>) =>
    onChange(lines.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const byCode = new Map(products.map((p) => [p.code, p]));
  const categories = new Map(products.flatMap((p) => p.categories).map((c) => [c.categoryId, c]));
  const visibleProducts = categoryId
    ? products.filter((p) => p.categories.some((c) => c.categoryId === categoryId))
    : products;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>اقلام</span>
          <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
            کالا، مقدار و واحد برای هر قلم الزامی است؛ جمع‌ها سمت سرور محاسبه می‌شود.
          </span>
        </div>
        {availableLists.length > 0 ? (
          <select
            className="sk-select"
            aria-label="لیست قیمت"
            style={{ minWidth: 160 }}
            value={priceListId}
            onChange={(e) => setPriceListId(e.target.value)}
          >
            <option value="">قیمت خود کالا</option>
            {availableLists.map((l) => (
              <option key={l.priceListId} value={l.priceListId}>
                {l.name}
              </option>
            ))}
          </select>
        ) : null}
        {categories.size > 0 ? (
          <select
            className="sk-select"
            aria-label="فیلتر کالا بر اساس دسته‌بندی"
            style={{ minWidth: 160 }}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">همهٔ دسته‌بندی‌ها</option>
            {[...categories.values()].map((c) => (
              <option key={c.categoryId} value={c.categoryId}>
                {c.name}
              </option>
            ))}
          </select>
        ) : null}
        <V2Button
          type="button"
          variant="secondary"
          size="sm"
          icon="plus"
          onClick={() => onChange([...lines, { ...EMPTY_QUOTE_LINE }])}
        >
          افزودن قلم
        </V2Button>
      </div>
      {priceListError ? (
        <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>
          بارگذاری لیست قیمت ناموفق بود؛ قیمت خود کالا استفاده می‌شود.
        </span>
      ) : null}
      <div className="sk-table-scroll">
        <table className="sk-table sk-table--compact">
          <thead>
            <tr>
              <th style={{ minWidth: 160 }}>کالا *</th>
              <th style={{ minWidth: 120 }}>شرح</th>
              <th style={{ width: 90 }}>مقدار *</th>
              <th style={{ width: 110 }}>واحد *</th>
              <th style={{ width: 120 }}>فی ({unitLabel})</th>
              <th style={{ width: 110 }}>تخفیف ({unitLabel})</th>
              {showVat ? <th style={{ width: 130 }}>مالیات</th> : null}
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td>
                  <select
                    className="sk-select"
                    style={{ width: '100%' }}
                    value={line.productCode}
                    onChange={(e) =>
                      update(i, {
                        productCode: e.target.value,
                        // The product's default unit and that unit's list price arrive on the line.
                        ...lineForProduct(byCode.get(e.target.value), prices),
                      })
                    }
                  >
                    <option value="">— انتخاب کالا —</option>
                    {line.productCode &&
                    !visibleProducts.some((p) => p.code === line.productCode) ? (
                      <option value={line.productCode}>
                        {byCode.get(line.productCode)?.name ?? line.productCode}
                      </option>
                    ) : null}
                    {visibleProducts.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name || p.code}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="sk-input"
                    style={{ width: '100%' }}
                    value={line.description}
                    onChange={(e) => update(i, { description: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    className="sk-input ltr sk-num"
                    style={{ width: '100%' }}
                    value={line.quantity}
                    onChange={(e) => update(i, { quantity: toAsciiDigits(e.target.value) })}
                  />
                </td>
                <td>
                  {(() => {
                    const product = byCode.get(line.productCode);
                    const units = sellableUnits(product);
                    const base = product?.units.find((u) => u.isBase);
                    // A unit already on a saved line stays selectable even if the catalogue dropped it.
                    const legacy = line.uomCode && !units.some((u) => u.uomCode === line.uomCode);
                    return (
                      <select
                        className="sk-select"
                        style={{ width: '100%' }}
                        value={line.uomCode}
                        disabled={!line.productCode}
                        onChange={(e) => update(i, lineForUnit(product, e.target.value, prices))}
                      >
                        <option value="">{line.productCode ? '— واحد —' : '— ابتدا کالا —'}</option>
                        {legacy ? <option value={line.uomCode}>{line.uomCode}</option> : null}
                        {units.map((u) => {
                          const hint = equivalenceHint(u, base);
                          return (
                            <option key={u.uomId} value={u.uomCode}>
                              {hint ? `${u.uomName} (${hint})` : u.uomName}
                            </option>
                          );
                        })}
                      </select>
                    );
                  })()}
                </td>
                <td>
                  <input
                    className="sk-input ltr sk-num"
                    style={{ width: '100%' }}
                    value={line.unitPriceRial}
                    onChange={(e) => update(i, { unitPriceRial: toAsciiDigits(e.target.value) })}
                  />
                  {(() => {
                    const listed = byCode
                      .get(line.productCode)
                      ?.units.find((u) => u.uomCode === line.uomCode)?.unitPriceRial;
                    return listed && listed !== line.unitPriceRial ? (
                      <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
                        قیمت لیست: {faDigits(Number(listed).toLocaleString('en-US'))}
                      </span>
                    ) : null;
                  })()}
                </td>
                <td>
                  <input
                    className="sk-input ltr sk-num"
                    style={{ width: '100%' }}
                    value={line.discountRial}
                    onChange={(e) => update(i, { discountRial: toAsciiDigits(e.target.value) })}
                  />
                </td>
                {showVat ? (
                  <td>
                    <select
                      className="sk-select"
                      style={{ width: '100%' }}
                      value={line.taxCode}
                      onChange={(e) => update(i, { taxCode: e.target.value })}
                    >
                      <option value="">— بدون مالیات —</option>
                      {taxes.map((t) => (
                        <option key={String(t.code)} value={String(t.code)}>
                          {String(t.name ?? t.code)}
                        </option>
                      ))}
                    </select>
                  </td>
                ) : null}
                <td>
                  {lines.length > 1 ? (
                    <button
                      type="button"
                      className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                      style={{ color: 'var(--sk-danger)' }}
                      aria-label="حذف قلم"
                      onClick={() => onChange(lines.filter((_, idx) => idx !== i))}
                    >
                      <Icon name="x" size="sm" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
